import React, { useState, useEffect } from 'react';
import { addDoc, updateDoc, doc, collection } from 'firebase/firestore';
import { db } from '../../../firebase';

export const SERVICE_OPTIONS = [
  'Lawn Mowing',
  'Hedge Trimming',
  'Tree Trimming',
  'Fertilizing',
  'Pre-Emergent / Fertilize',
  'Weed Control',
  'Aeration',
  'Seeding',
  'Lighting',
  'Planting',
  'Other'
];

const emptyLineItem = () => ({
  serviceType: SERVICE_OPTIONS[0],
  description: '',
  quantity: 1,
  unitPrice: ''
});

const BidModal = ({ onClose, customers, editingBid }) => {
  const isEdit = Boolean(editingBid?.id);
  const [customerId, setCustomerId] = useState(editingBid?.customerId ?? '');
  const [customerName, setCustomerName] = useState(editingBid?.customerName ?? '');
  const [email, setEmail] = useState(editingBid?.email ?? '');
  const [phone, setPhone] = useState(editingBid?.phone ?? '');
  const [address, setAddress] = useState(editingBid?.address ?? '');
  const [lineItems, setLineItems] = useState(
    editingBid?.lineItems?.length
      ? editingBid.lineItems.map(li => ({
          serviceType: li.serviceType || SERVICE_OPTIONS[0],
          description: li.description || '',
          quantity: Number(li.quantity) || 1,
          unitPrice: li.unitPrice !== undefined && li.unitPrice !== '' ? String(li.unitPrice) : ''
        }))
      : [emptyLineItem()]
  );
  const [notes, setNotes] = useState(editingBid?.notes ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    const c = customers.find(x => x.id === customerId);
    if (!c) return;
    setCustomerName(c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim());
    setEmail(c.email || '');
    setPhone(c.phone || '');
    setAddress(c.address || '');
  }, [customerId, customers]);

  const addLine = () => setLineItems(prev => [...prev, emptyLineItem()]);
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };
  const updateLine = (index, field, value) => {
    setLineItems(prev => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  };

  const total = lineItems.reduce((sum, line) => {
    const q = Number(line.quantity) || 0;
    const p = parseFloat(line.unitPrice) || 0;
    return sum + q * p;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = customerName.trim();
    if (!name) {
      alert('Please enter customer name.');
      return;
    }
    const items = lineItems.map(li => ({
      serviceType: li.serviceType,
      description: (li.description || '').trim(),
      quantity: Number(li.quantity) || 1,
      unitPrice: parseFloat(li.unitPrice) || 0
    }));
    if (items.every(i => i.unitPrice === 0)) {
      alert('Add at least one line item with a price.');
      return;
    }
    const bidTotal = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
    setSaving(true);
    try {
      const data = {
        customerId: customerId || null,
        customerName: name,
        email: email.trim() || null,
        phone: phone.trim() || null,
        address: address.trim() || null,
        lineItems: items,
        notes: notes.trim() || null,
        total: bidTotal,
        status: editingBid?.status ?? 'draft',
        updatedAt: new Date()
      };
      if (isEdit) {
        await updateDoc(doc(db, 'bids', editingBid.id), data);
      } else {
        data.createdAt = new Date();
        await addDoc(collection(db, 'bids'), data);
      }
      onClose();
    } catch (err) {
      console.error(err);
      alert('Failed to save bid.');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-4 sm:px-6 py-4 border-b border-secondary-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-secondary-900">
              {isEdit ? 'Edit bid' : 'New bid'}
            </h2>
            <p className="text-sm text-secondary-600 mt-0.5">Customer info and service line items.</p>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1 space-y-6">
              {/* Customer */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-secondary-800">Customer</h3>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Existing customer (optional)</label>
                  <select
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">— New lead —</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Unknown'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="input w-full"
                      placeholder="Customer or company name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input w-full"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="input w-full"
                      placeholder="(555) 000-0000"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="input w-full"
                      placeholder="Street, city, state, zip"
                    />
                  </div>
                </div>
              </div>

              {/* Line items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-secondary-800">Services</h3>
                  <button type="button" onClick={addLine} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                    + Add line
                  </button>
                </div>
                <div className="border border-secondary-200 rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-secondary-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-secondary-600">Service</th>
                        <th className="px-3 py-2 text-left font-medium text-secondary-600">Details</th>
                        <th className="px-3 py-2 text-right font-medium text-secondary-600 w-20">Qty</th>
                        <th className="px-3 py-2 text-right font-medium text-secondary-600 w-24">Unit $</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {lineItems.map((line, index) => (
                        <tr key={index} className="bg-white">
                          <td className="px-3 py-2">
                            <select
                              value={line.serviceType}
                              onChange={(e) => updateLine(index, 'serviceType', e.target.value)}
                              className="input py-1.5 text-sm w-full"
                            >
                              {SERVICE_OPTIONS.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={line.description}
                              onChange={(e) => updateLine(index, 'description', e.target.value)}
                              className="input py-1.5 text-sm w-full"
                              placeholder="Notes"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              min="1"
                              value={line.quantity}
                              onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                              className="input py-1.5 text-sm w-16 text-right"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.unitPrice}
                              onChange={(e) => updateLine(index, 'unitPrice', e.target.value)}
                              className="input py-1.5 text-sm w-20 text-right"
                              placeholder="0"
                            />
                          </td>
                          <td className="px-1 py-2">
                            <button
                              type="button"
                              onClick={() => removeLine(index)}
                              disabled={lineItems.length <= 1}
                              className="p-1 text-secondary-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                              aria-label="Remove line"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-right font-semibold text-secondary-900">
                  Total: {formatCurrency(total)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input w-full min-h-[80px]"
                  placeholder="Terms, validity, or other notes for this bid"
                  rows={3}
                />
              </div>
            </div>
            <div className="px-4 sm:px-6 py-4 border-t border-secondary-200 flex-shrink-0 flex gap-3 justify-end">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Saving…' : isEdit ? 'Update bid' : 'Save bid'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BidModal;
